/**
 * API service for managing roster assignments (worker allocations to Work Orders)
 */
import axios from "../utils/api-client";

const API_BASE_URL = `${process.env.REACT_APP_API_END_POINT}/RosterAssignments`;

/** A person's base: the line and the shift ("1st", "2nd", "12h day") they work most. */
export interface HomeCrewMember {
	workerId: string;
	workerName: string;
	workstation: string;
	shiftLabel: string | null;
	shiftPatternId?: number | null;
	/** The person's own shifts in the window asked for (plant-local ISO). */
	shifts?: { shiftId: string; start: string; end: string }[];
}

export interface RosterAssignment {
	id: number;
	workOrderId: number;
	/** @deprecated Use workOrderId instead */
	manufacturingOrderId?: number;
	laborStandardPhaseId?: number;
	workerId: string;
	workerName: string;
	jobRoleId?: number;
	jobRoleName: string;
	startTime: string;
	endTime: string;
	/**
	 * The work center of the step this assignment staffs, copied from the step
	 * so the roster no longer has to infer it from the order.
	 */
	workstation?: string | null;
	/**
	 * A phase that crosses a shift handover is assigned per shift segment;
	 * 1..n. Null for a phase that fits inside one shift.
	 */
	segmentSeq?: number | null;
	isReallocated: boolean;
	isManuallyAssigned: boolean;
	originalShiftId?: string;
	shiftId?: string;
	assignedBy?: string;
	notes?: string;
	createdDate: string;
	modifiedDate?: string;
}

export interface AllocationWarning {
	type: string;
	message: string;
	operationName?: string;
	phaseName?: string;
	phaseId?: number;
	workersNeeded: number;
	workersAssigned: number;
}

export interface AllocationSummary {
	totalPhasesRequired: number;
	totalPhasesFilled: number;
	totalWorkersNeeded: number;
	totalWorkersAssigned: number;
	reallocatedWorkers: number;
	isFullyStaffed: boolean;
}

export interface AllocationResult {
	success: boolean;
	assignments: RosterAssignment[];
	warnings: AllocationWarning[];
	summary: AllocationSummary;
}

export interface BatchAllocationSummary {
	totalWorkOrders: number;
	/** @deprecated Use totalWorkOrders instead */
	totalMOs?: number;
	fullyStaffedWorkOrders: number;
	/** @deprecated Use fullyStaffedWorkOrders instead */
	fullyStaffedMOs?: number;
	partiallyStaffedWorkOrders: number;
	/** @deprecated Use partiallyStaffedWorkOrders instead */
	partiallyStaffedMOs?: number;
	unstaffedWorkOrders: number;
	/** @deprecated Use unstaffedWorkOrders instead */
	unstaffedMOs?: number;
	totalWorkersAssigned: number;
	totalReallocatedWorkers: number;
	warnings?: string[];
}

export interface BatchAllocationResult {
	success: boolean;
	summary: BatchAllocationSummary;
}

export interface AvailableWorker {
	workerId: string;
	firstName: string;
	lastName: string;
	fullName: string;
	eligibleJobIds: number[];
	seniorityInDays: number;
	shiftId?: string;
	shiftStartTime?: string;
	shiftEndTime?: string;
	primaryJobId?: number;
}

export interface ManualAssignmentRequest {
	workOrderId: number;
	/** @deprecated Use workOrderId instead */
	manufacturingOrderId?: number;
	phaseId?: number;
	workerId: string;
	workerName: string;
	jobRoleId?: number;
	jobRoleName: string;
	startTime: string;
	endTime: string;
	shiftId?: string;
	/** Placed outside the worker's own shift (the fill picker's "off shift" candidates). */
	isOvertime?: boolean;
	notes?: string;
}

export interface UpdateAssignmentRequest {
	workerId?: string;
	workerName?: string;
	jobRoleId?: number;
	jobRoleName?: string;
	phaseId?: number;
	notes?: string;
}


// ---------------------------------------------------------------------------
// Manual control: move / pin / fill / standing crew
// ---------------------------------------------------------------------------

/** How a step phase would take the dragged assignment. */
export type PhaseTargetState = "ok" | "full" | "unqualified" | "current";
/** How a worker would take the dragged assignment. */
export type WorkerTargetState = "free" | "busy" | "unqualified" | "current";

export interface MoveTargetPhase {
	workOrderId: number;
	phaseId: number;
	workstation?: string | null;
	jobRole?: string | null;
	state: PhaseTargetState;
	/** Heads already on the phase / heads the standard asks for. */
	have?: number;
	needed?: number;
	reason?: string | null;
	/** The step's product, for "Swab on Glatt". */
	productName?: string | null;
	/** The step's planned window (plant-local ISO); end may be open. */
	stepStart?: string | null;
	stepEnd?: string | null;
}

export interface MoveTargetWorker {
	workerId: string;
	workerName: string;
	state: WorkerTargetState;
	reason?: string | null;
}

/** Server shape of GET /RosterAssignments/{id}/move-targets (phases grouped by step). */
interface MoveTargetsResponse {
	assignmentId: number;
	steps?: Array<{
		workOrderId: number;
		workstation?: string | null;
		productName?: string | null;
		isCurrent?: boolean;
		startTime?: string | null;
		endTime?: string | null;
		phases?: Array<{
			phaseId: number;
			operationName?: string | null;
			jobRole?: string | null;
			jobRoleId?: number | null;
			needed: number;
			have: number;
			state: PhaseTargetState;
			reason?: string | null;
		}>;
	}>;
	workers?: Array<{
		workerId: string;
		workerName: string;
		shiftId?: string | null;
		state: WorkerTargetState;
		reason?: string | null;
		hoursThatDay?: number;
	}>;
}

/** Flattened move targets as the board indexes them. */
export interface MoveTargets {
	phases: MoveTargetPhase[];
	workers: MoveTargetWorker[];
}

/** Move the worker to another step from a time inside their block; the earlier part stays. */
export interface SplitMoveRequest {
	/** Plant-local ISO without zone, e.g. "2026-10-06T10:30:00". */
	at: string;
	/** Set = a move for a while: back on their own step from this time (plant-local ISO). */
	until?: string;
	targetWorkOrderId: number;
	targetPhaseId?: number;
	allowUnqualified?: boolean;
	allowOvertime?: boolean;
}

export interface SplitMoveResult {
	stayedAssignmentId: number;
	/** A lend's last part, back on the original step. */
	returnAssignmentId?: number | null;
	moved: MoveResult;
}

export interface MoveRequest {
	targetWorkOrderId?: number;
	targetPhaseId?: number;
	targetWorkerId?: string;
	targetWorkerName?: string;
	/** Move every segment / every assignment of the worker on the step together. */
	wholeStep?: boolean;
	allowUnqualified?: boolean;
	allowOvertime?: boolean;
}

/** The hole a move leaves behind on the step the worker came from. */
export interface MoveGap {
	workOrderId: number;
	phaseId: number;
	workstation: string;
	jobRole: string;
	startTime: string;
	endTime: string;
	needed: number;
	have: number;
}

export interface MoveResult {
	movedAssignmentIds: number[];
	/** Parts of the block left on their step when the target runs for only part of it. */
	stayedAssignmentIds?: number[];
	warnings: string[];
	gap?: MoveGap | null;
}

export interface ClearRangeResult {
	deleted: number;
	keptManual: number;
}

/** Where a background fill of many days has got to (server: RosterFillProgress). */
export interface FillRangeProgress {
	jobId: string;
	status: "running" | "done" | "failed";
	from: string;
	to: string;
	locationIds: number[];
	total: number;
	done: number;
	/** "2026-10-26 · 345" while running. */
	current?: string | null;
	assigned: number;
	errors: string[];
	startedAt: string;
	finishedAt?: string | null;
}

export interface SuggestParams {
	workOrderId: number;
	phaseId?: number;
	start: string;
	end: string;
	locationId: number;
	/** Also grade people on another of the plant's shifts that day; they come back as `state: "overtime"`. */
	includeOffShift?: boolean;
	/** How many people to grade back (the server's default is 6). */
	take?: number;
}

export interface SuggestCandidate {
	workerId: string;
	workerName: string;
	shiftId?: string | null;
	/** "free" | "busy" | "overtime" | "unqualified" … as the server grades it. */
	state: string;
	reason?: string | null;
	hoursThatDay: number;
	/** Set when the person is from a sister plant of the location cluster, e.g. "3725". */
	fromPlant?: string | null;
}

export interface SuggestResult {
	candidates: SuggestCandidate[];
}

/** A standing crew pin: worker x work center (x role) for a date range. */
export interface StandingPin {
	id: number;
	workerId: string;
	workerName: string;
	workstation: string;
	jobRoleId?: number | null;
	jobRoleName?: string | null;
	startDate: string;
	endDate: string;
	notes?: string | null;
	/** Who placed the pin, when the server records it. */
	createdBy?: string | null;
	createdDate?: string | null;
}

export type CreateStandingPinRequest = Omit<StandingPin, "id" | "createdBy" | "createdDate">;

/** Shorten (or annotate) a standing pin. */
export interface UpdateStandingPinRequest {
	endDate?: string;
	notes?: string;
}

/**
 * Keep a block longer: "week" = through the Sunday of the block's week,
 * "until" = through `untilDate` (yyyy-MM-dd).
 */
export interface ExtendRequest {
	scope: "week" | "until";
	untilDate?: string;
	notes?: string;
}

export interface ExtendResult {
	pinId: number;
	startDate: string;
	endDate: string;
	/** Already-allocated days in the range that were re-pinned onto this line. */
	pinnedDays: number;
	/** Auto blocks of the worker elsewhere on those days that were removed. */
	displacedBlocks: number;
	/** Overlapping pins of the same worker that were cut. */
	pinsShortened: number;
	warnings: string[];
}

// ---------------------------------------------------------------------------
// Availability: who is out (or asking to be) on a shift
// ---------------------------------------------------------------------------

/**
 * Why a rostered worker is not expected on a shift. Every kind but
 * `requested` is CONFIRMED unavailability; `requested` is a pending time-off
 * or call-out request — the person is still expected unless it is approved.
 */
export type AvailabilityKind = "timeOff" | "training" | "restrictedDuty" | "reassigned" | "absent" | "requested" | "unavailable";

export type AvailabilityRequestStatus = "Pending" | "Approved";

/** One row of GET /RosterAssignments/availability: a worker × shift the plant should not count on. */
export interface AvailabilityRow {
	workerId: string;
	workerName: string;
	/** The shift instance the row is about; null when the source only knows the day. */
	shiftId?: string | null;
	/** yyyy-MM-dd, plant-local. */
	date: string;
	/** Plant-local ISO window. */
	startTime: string;
	endTime: string;
	kind: AvailabilityKind;
	/** As the source names it: "Time Off", "Leave Request", "Training"… */
	label: string;
	requestStatus?: AvailabilityRequestStatus | null;
	detail?: string | null;
}

/** Server shape: the rows sit under `rows`. */
interface AvailabilityResponse {
	rows?: AvailabilityRow[] | null;
}

export const rosterAssignmentsApi = {
	/**
	 * Get all assignments for a specific Work Order
	 */
	getByWorkOrder: async (workOrderId: number): Promise<RosterAssignment[]> => {
		const response = await axios.get<RosterAssignment[]>(`${API_BASE_URL}/mo/${workOrderId}`);
		return response.data;
	},

	/** @deprecated Use getByWorkOrder instead */
	getByMO: async (moId: number): Promise<RosterAssignment[]> => {
		return rosterAssignmentsApi.getByWorkOrder(moId);
	},

	/**
	 * Get all assignments for a date range
	 */
	getByDateRange: async (startDate: string, endDate: string): Promise<RosterAssignment[]> => {
		const response = await axios.get<RosterAssignment[]>(`${API_BASE_URL}/range`, {
			params: { startDate, endDate },
		});
		return response.data;
	},

	/**
	 * Trigger allocation for a specific Work Order
	 */
	allocateWorkOrder: async (workOrderId: number, locationId: number): Promise<AllocationResult> => {
		const response = await axios.post<AllocationResult>(`${API_BASE_URL}/allocate/${workOrderId}`, null, {
			params: { locationId },
		});
		return response.data;
	},

	/** @deprecated Use allocateWorkOrder instead */
	allocateMO: async (moId: number, locationId: number): Promise<AllocationResult> => {
		return rosterAssignmentsApi.allocateWorkOrder(moId, locationId);
	},

	/**
	 * Trigger allocation for all Work Orders on a specific date.
	 *
	 * The date is normalised to a LOCAL calendar day (yyyy-MM-dd). Callers used to
	 * pass `someDate.toISOString()`, which shifts to UTC — so west of Greenwich any
	 * allocation run in the evening silently targeted TOMORROW and reported "0 job
	 * orders" while the plan on screen stayed untouched. The roster is a local
	 * calendar, so only the local day is ever meaningful here.
	 */
	allocateForDate: async (
		date: string | Date,
		locationId: number
	): Promise<BatchAllocationResult> => {
		// An already-plain calendar day is passed straight through: `new Date()`
		// would read it as UTC midnight and shift it a day backwards west of
		// Greenwich, reintroducing the very bug this guards against.
		if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
			const passthrough = await axios.post<BatchAllocationResult>(
				`${API_BASE_URL}/allocate-date`,
				null,
				{ params: { date, locationId } }
			);
			return passthrough.data;
		}

		const value = typeof date === "string" ? new Date(date) : date;
		const localDay = [
			value.getFullYear(),
			String(value.getMonth() + 1).padStart(2, "0"),
			String(value.getDate()).padStart(2, "0"),
		].join("-");

		const response = await axios.post<BatchAllocationResult>(`${API_BASE_URL}/allocate-date`, null, {
			params: { date: localDay, locationId },
		});
		return response.data;
	},

	/**
	 * Fill the roster for a range of days (yyyy-MM-dd, inclusive) in the background,
	 * with includeCluster for the plants that share people too. Returns the job to
	 * poll with getFillRange; a company runs one fill at a time.
	 */
	startFillRange: async (from: string, to: string, locationId: number, includeCluster: boolean): Promise<FillRangeProgress> => {
		const response = await axios.post<FillRangeProgress>(`${API_BASE_URL}/allocate-range`, null, {
			params: { from, to, locationId, includeCluster },
		});
		return response.data;
	},

	getFillRange: async (jobId: string): Promise<FillRangeProgress> => {
		const response = await axios.get<FillRangeProgress>(`${API_BASE_URL}/allocate-range/${jobId}`);
		return response.data;
	},

	/**
	 * Manually assign a worker to a position
	 */
	manualAssign: async (request: ManualAssignmentRequest): Promise<RosterAssignment> => {
		const response = await axios.post<RosterAssignment>(`${API_BASE_URL}/manual`, request);
		return response.data;
	},

	/**
	 * Update an existing assignment
	 */
	update: async (id: number, request: UpdateAssignmentRequest): Promise<RosterAssignment> => {
		const response = await axios.put<RosterAssignment>(`${API_BASE_URL}/${id}`, request);
		return response.data;
	},

	/**
	 * Delete an assignment
	 */
	delete: async (id: number): Promise<void> => {
		await axios.delete(`${API_BASE_URL}/${id}`);
	},

	/** Remove a block from its step; the crew line opens up and Allocate may refill it. */
	remove: async (id: number): Promise<void> => {
		await axios.delete(`${API_BASE_URL}/${id}`);
	},

	/**
	 * Clear a plant's roster for a day range (dates inclusive): allocated blocks
	 * go, manual placements stay unless `includeManual`. Standing pins stay.
	 */
	clearRange: async (locationId: number, from: string, to: string, includeManual: boolean): Promise<ClearRangeResult> => {
		const response = await axios.delete<ClearRangeResult>(`${API_BASE_URL}/range`, {
			params: { locationId, from, to, includeManual },
		});
		return response.data;
	},

	/**
	 * Get available workers for a specific time slot and job role
	 */
	getAvailableWorkers: async (
		startTime: string,
		endTime: string,
		jobRoleId: number,
		locationId: number
	): Promise<AvailableWorker[]> => {
		const response = await axios.get<AvailableWorker[]>(`${API_BASE_URL}/available-workers`, {
			params: { startTime, endTime, jobRoleId, locationId },
		});
		return response.data;
	},

	// ---- Manual control -----------------------------------------------------

	/** Every step phase and worker the assignment could be moved to, graded. */
	getMoveTargets: async (id: number, locationId: number): Promise<MoveTargets> => {
		// The server groups phases under their step; the board indexes a flat list.
		const response = await axios.get<MoveTargetsResponse>(`${API_BASE_URL}/${id}/move-targets`, {
			params: { locationId },
		});
		const data = response.data;
		return {
			phases: (data.steps ?? []).flatMap((step) =>
				(step.phases ?? []).map((phase) => ({
					workOrderId: step.workOrderId,
					phaseId: phase.phaseId,
					workstation: step.workstation,
					jobRole: phase.jobRole,
					state: phase.state,
					have: phase.have,
					needed: phase.needed,
					reason: phase.reason,
					productName: step.productName,
					stepStart: step.startTime,
					stepEnd: step.endTime,
				}))
			),
			workers: (data.workers ?? []).map((worker) => ({
				workerId: worker.workerId,
				workerName: worker.workerName,
				state: worker.state,
				reason: worker.reason,
			})),
		};
	},

	/**
	 * Move an assignment to another worker or another step phase. Saved at
	 * once and pinned. A 409 carries `{ message }`: a hard block (double
	 * booking) or a confirmation request ("Confirm to place anyway" /
	 * "Confirm to place as overtime") to re-send with the matching allow flag.
	 */
	move: async (id: number, locationId: number, body: MoveRequest): Promise<MoveResult> => {
		const response = await axios.post<MoveResult>(`${API_BASE_URL}/${id}/move`, body, {
			params: { locationId },
		});
		return response.data;
	},

	/** Let Allocate change this assignment again. */
	/** Cut the block at a time and move the rest to another step. Pieces must be two hours or more. */
	splitMove: async (id: number, locationId: number, body: SplitMoveRequest): Promise<SplitMoveResult> => {
		const response = await axios.post<SplitMoveResult>(`${API_BASE_URL}/${id}/split-move`, body, {
			params: { locationId },
		});
		return response.data;
	},

	unpin: async (id: number): Promise<void> => {
		await axios.post(`${API_BASE_URL}/${id}/unpin`);
	},

	/** Who could fill a phase window, graded. */
	suggest: async (params: SuggestParams): Promise<SuggestResult> => {
		const response = await axios.get<SuggestResult>(`${API_BASE_URL}/suggest`, { params });
		return response.data;
	},

	/**
	 * Who is out, or asking to be, on the plant's shifts between two days
	 * (yyyy-MM-dd, inclusive). Flattened to the rows; a server that sends no
	 * `rows` means nobody is out.
	 */
	/** Each line's own people by shift (home work center and crew), for the shift roster. */
	getHomeCrews: async (locationId: number, from: string, to: string): Promise<HomeCrewMember[]> => {
		const response = await axios.get<HomeCrewMember[]>(`${API_BASE_URL}/home-crews`, { params: { locationId, from, to } });
		return (response.data ?? []).map((m) => ({ ...m, workerId: String(m.workerId) }));
	},

	getAvailability: async (locationId: number, from: string, to: string): Promise<AvailabilityRow[]> => {
		const response = await axios.get<AvailabilityResponse>(`${API_BASE_URL}/availability`, {
			params: { locationId, from, to },
		});
		return (response.data?.rows ?? []).map((row) => ({
			...row,
			workerId: String(row.workerId),
			shiftId: row.shiftId != null ? String(row.shiftId) : null,
		}));
	},

	getPins: async (locationId: number, from: string, to: string): Promise<StandingPin[]> => {
		const response = await axios.get<StandingPin[]>(`${API_BASE_URL}/pins`, {
			params: { locationId, from, to },
		});
		return response.data;
	},

	createPin: async (request: CreateStandingPinRequest, locationId: number): Promise<StandingPin> => {
		const response = await axios.post<StandingPin>(`${API_BASE_URL}/pins`, request, {
			params: { locationId },
		});
		return response.data;
	},

	deletePin: async (id: number): Promise<void> => {
		await axios.delete(`${API_BASE_URL}/pins/${id}`);
	},

	/** Shorten a pin (or change its notes). */
	updatePin: async (id: number, body: UpdateStandingPinRequest): Promise<StandingPin> => {
		const response = await axios.put<StandingPin>(`${API_BASE_URL}/pins/${id}`, body);
		return response.data;
	},

	/**
	 * Pin the block and keep the worker on its line for the week or until a
	 * date: creates a standing pin, re-pins already-allocated days in the
	 * range (removing the worker's auto blocks elsewhere those days) and cuts
	 * the worker's overlapping pins. A 409 carries `{ message }` on bad dates.
	 */
	extend: async (id: number, locationId: number, body: ExtendRequest): Promise<ExtendResult> => {
		const response = await axios.post<ExtendResult>(`${API_BASE_URL}/${id}/extend`, body, {
			params: { locationId },
		});
		return response.data;
	},
};

export default rosterAssignmentsApi;
